import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { useCustomers } from '../hooks/useCustomers';
import { Navbar } from '../components/Navbar';
import { CustomerCard } from '../components/CustomerCard';
import { BackButton, Badge, Button, EmptyState, FAB, SkeletonCard } from '../components/core';
import { Input } from '../components/core';
import { Icon, SearchIcon, PlusIcon, CloseIcon } from '../components/core';
import { subscribeToRegions, type Region } from '../services/region.service';
import { getCustomerIdsByRegion } from '../services/cropStatus.service';

export default function CustomerListPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const { customers, loading, error } = useCustomers(orgId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [regionId, setRegionId] = useState(searchParams.get('region') ?? '');
  const [regions, setRegions] = useState<Region[]>([]);
  const [filteredCustomerIds, setFilteredCustomerIds] = useState<string[] | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Subscribe to regions list
  useEffect(() => {
    const unsubscribe = subscribeToRegions(
      orgId,
      data => setRegions(data),
      () => setRegions([]),
    );
    return unsubscribe;
  }, [orgId]);

  // Debounce text search and persist to URL
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      const params: Record<string, string> = {};
      if (search) params.q = search;
      if (regionId) params.region = regionId;
      setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, regionId, setSearchParams]);

  // Persist region filter to URL immediately on change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (regionId) params.region = regionId;
    setSearchParams(params, { replace: true });
  }, [regionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch matching customer IDs when region filter changes
  useEffect(() => {
    if (!regionId) {
      setFilteredCustomerIds(null);
      return;
    }
    let cancelled = false;
    getCustomerIdsByRegion(orgId, regionId).then(ids => {
      if (!cancelled) setFilteredCustomerIds(ids);
    }).catch(() => {
      if (!cancelled) setFilteredCustomerIds([]);
    });
    return () => { cancelled = true; };
  }, [orgId, regionId]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    let result = customers;

    if (term) {
      result = result.filter(
        c =>
          c.firstName.toLowerCase().includes(term) ||
          c.lastName.toLowerCase().includes(term) ||
          c.phone.includes(term),
      );
    }

    if (filteredCustomerIds !== null) {
      const idSet = new Set(filteredCustomerIds);
      result = result.filter(c => idSet.has(c.id));
    }

    return result;
  }, [customers, debouncedSearch, filteredCustomerIds]);

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar
        displayName={user?.displayName ?? null}
        onSignOut={signOut}
        onLogoClick={() => navigate('/')}
        onOrgNameClick={() => navigate(`/org/${orgSlug}`)}
        onAvatarClick={() => navigate('/profile')}
        orgName={org.name}
      />

      <main className="mx-auto max-w-2xl px-4 py-6 pb-24 sm:px-6 sm:pb-6">
        <BackButton />

        {/* Page heading */}
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-display text-xl font-semibold text-gray-900">
              {t('customers.title')}
              <Badge>{customers.length}</Badge>
            </h1>
            <p className="mt-1 font-body text-sm text-gray-500">
              {t('customers.subtitle')}
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={
              <Icon size="sm">
                <PlusIcon />
              </Icon>
            }
            onClick={() => navigate(`/org/${orgSlug}/customers/new`)}
            className="hidden shrink-0 sm:inline-flex"
          >
            {t('customers.add')}
          </Button>
        </div>

        {/* Filter bar */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-[4]">
            <Input
              placeholder={t('customers.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={
                <Icon size="sm" className="text-gray-400">
                  <SearchIcon />
                </Icon>
              }
              className={search ? 'pe-9' : ''}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute end-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
              >
                <Icon size="xs">
                  <CloseIcon />
                </Icon>
              </button>
            )}
          </div>

          {regions.length > 0 && (
            <select
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              className="min-w-0 flex-[2] appearance-none rounded-sm border border-gray-300 bg-white px-3 py-[11px] font-body text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
            >
              <option value="">{t('customers.allRegions')}</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Customer list */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {t('customers.loadError')}
          </div>
        ) : filtered.length === 0 && (debouncedSearch || regionId) ? (
          <div className="py-16 text-center">
            <p className="font-body text-base font-semibold text-gray-700">
              {t('customers.noResults', { term: debouncedSearch })}
            </p>
            <p className="mt-1 font-body text-sm text-gray-400">
              {t('customers.noResultsHelp')}
            </p>
          </div>
        ) : customers.length === 0 ? (
          <EmptyState
            emoji="🌱"
            title={t('customers.noCustomers')}
            description={t('customers.noCustomersHelp')}
            action={
              <Button
                variant="primary"
                icon={
                  <Icon size="sm">
                    <PlusIcon />
                  </Icon>
                }
                onClick={() => navigate(`/org/${orgSlug}/customers/new`)}
              >
                {t('customers.add')}
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((customer) => (
              <CustomerCard
                key={customer.id}
                firstName={customer.firstName}
                lastName={customer.lastName}
                phone={customer.phone}
                onClick={() => navigate(`/org/${orgSlug}/customers/${customer.id}`)}
              />
            ))}
          </div>
        )}
      </main>

      <FAB
        ariaLabel={t('fab.ariaLabel')}
        onClick={() => navigate(`/org/${orgSlug}/customers/new`)}
      />
    </div>
  );
}